import React, { createRef } from 'react'
import { Link } from 'react-router-dom'
import { BaseView, baseStateToProps } from '../smart_bps/lib/base_view'
import { wrapView } from '../smart_bps/lib/utils'
import { AuthorizedDropDownMeny, UnauthorizedDropDownMenu } from '../lib/NavDropdownMenu'
import { initSigurSelect2 } from '../lib/select2Modal'
import PeriodRangeField from './PeriodRangeField'
import {
  fetchRequestControlMeta,
  fetchRequestControlSummary,
  fetchRequestControlDetail,
} from './api'
import {
  emptyFilters,
  filtersFromMeta,
  mergeCachedFilters,
  filtersEqual,
  loadRequestControlCache,
  saveRequestControlCache,
  clearRequestControlCache,
} from './requestControlCache'

function formatDateIso(iso) {
  if (!iso) return ''
  const parts = String(iso).split('T')[0].split('-')
  if (parts.length < 3) return iso
  return `${parts[2]}.${parts[1]}.${parts[0]}`
}

/** Заголовок столбца отчёта в две строки: «Кол-во заявок» + «(Черновик)». */
function formatReportHeaderTitle(title) {
  if (!title) return title
  const paren = title.indexOf(' (')
  if (paren > 0) {
    return (
      <>
        {title.slice(0, paren)}
        <br />
        <span className="report-th-sub">{title.slice(paren + 1)}</span>
      </>
    )
  }
  const words = title.split(/\s+/)
  if (words.length >= 3) {
    const mid = Math.ceil(words.length / 2)
    return (
      <>
        {words.slice(0, mid).join(' ')}
        <br />
        {words.slice(mid).join(' ')}
      </>
    )
  }
  return title
}

/** Столбцы сводки, в которых есть хотя бы одно ненулевое значение. */
function getVisibleSummaryColumns(summary) {
  if (!summary?.columns?.length) return []
  return summary.columns.filter((col) => {
    if ((summary.totals?.[col.id] ?? 0) > 0) return true
    return (summary.rows || []).some((row) => (row.cells?.[col.id] ?? 0) > 0)
  })
}

function isSummaryEmpty(summary) {
  if (!summary?.rows?.length) return true
  return (summary.columns || []).every((col) => {
    if ((summary.totals?.[col.id] ?? 0) > 0) return false
    return !(summary.rows || []).some((row) => (row.cells?.[col.id] ?? 0) > 0)
  })
}

function applySelect2Values($sel, name, filters) {
  if (!filters || !name) return
  const raw = filters[name]
  if ($sel.prop('multiple')) {
    const vals = Array.isArray(raw) ? raw.map(String) : raw != null && raw !== '' ? [String(raw)] : []
    $sel.val(vals)
    return
  }
  $sel.val(raw != null && raw !== '' ? String(raw) : '')
}

function refreshSelect2Display($sel) {
  if ($sel.data('select2')) {
    $sel.trigger('change.select2')
  }
}

function initSelect2InForm(formEl, onChange, filters) {
  if (!formEl || typeof window.$ === 'undefined') return
  const $ = window.$
  $(formEl)
    .find('select.js-select2-report')
    .each(function () {
      const $sel = $(this)
      if ($sel.data('select2')) {
        $sel.off('change')
      }
      initSigurSelect2($sel)
      const name = this.getAttribute('name')
      applySelect2Values($sel, name, filters)
      refreshSelect2Display($sel)
      if (onChange && name) {
        $sel.on('change', function () {
          let val = $(this).val()
          if (this.multiple) {
            val = val ? (Array.isArray(val) ? val : [val]) : []
          }
          onChange(name, val)
        })
      }
    })
}

function destroySelect2InForm(formEl) {
  if (!formEl || typeof window.$ === 'undefined') return
  const $ = window.$
  $(formEl)
    .find('select.js-select2-report')
    .each(function () {
      const $sel = $(this)
      if ($sel.data('select2')) $sel.select2('destroy')
    })
}


class RequestControlReport extends BaseView {
  formRef = createRef()

  renderTopPanelRightItems() {
    if (this.authorized) {
      return (
        <AuthorizedDropDownMeny
          userTitle={this.user_title}
          onLogout={this.logout}
        />
      )
    }
    return <UnauthorizedDropDownMenu />
  }

  renderBreadcrumbs() {
    return (
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <Link to="/">Домой</Link>
          </li>
          <li className="breadcrumb-item">
            <Link to="/reports">Отчёты</Link>
          </li>
          <li className="breadcrumb-item active">Контроль заявок</li>
        </ol>
      </nav>
    )
  }

  state = {
    meta: null,
    metaLoading: true,
    filters: emptyFilters(),
    summary: null,
    summaryLoading: false,
    detail: null,
    detailTitle: '',
    detailLoading: false,
    displayMode: 'summary',
    resultsSlideInstant: false,
    formError: null,
  }

  _resultsSlideTimer = null
  _select2InitRaf = null

  static SLIDE_MS = 360

  scheduleSelect2Init() {
    if (this._select2InitRaf != null) {
      cancelAnimationFrame(this._select2InitRaf)
    }
    this._select2InitRaf = requestAnimationFrame(() => {
      this._select2InitRaf = requestAnimationFrame(() => {
        this._select2InitRaf = null
        this.initSelect2()
      })
    })
  }

  formNeedsSelect2Init() {
    const formEl = this.formRef.current
    if (!formEl || typeof window.$ === 'undefined') return false
    const $ = window.$
    let needs = false
    $(formEl)
      .find('select.js-select2-report')
      .each(function () {
        if (!$(this).data('select2')) {
          needs = true
          return false
        }
      })
    return needs
  }

  persistFiltersCache = (filters) => {
    saveRequestControlCache({ filters })
  }

  persistReportCache = (patch) => {
    saveRequestControlCache({
      filters: this.state.filters,
      ...patch,
    })
  }

  componentDidMount() {
    fetchRequestControlMeta((err, meta) => {
      if (err) {
        this.setState({ metaLoading: false, formError: String(err) })
        return
      }
      const cached = loadRequestControlCache()
      const filters = mergeCachedFilters(meta, cached?.filters)
      const canRestoreSummary =
        cached?.summary &&
        cached.appliedFilters &&
        filtersEqual(filters, cached.appliedFilters)

      this.setState(
        {
          meta,
          metaLoading: false,
          filters,
          summary: canRestoreSummary ? cached.summary : null,
          displayMode: 'summary',
        },
        () => {
          this.scheduleSelect2Init()
          if (canRestoreSummary) {
            this.persistReportCache({
              filters,
              appliedFilters: filters,
              summary: cached.summary,
              displayMode: 'summary',
            })
          } else {
            this.handleApply()
          }
        }
      )
    })
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.metaLoading) return
    const metaReady = prevState.metaLoading && !this.state.metaLoading
    const applyFinished = prevState.summaryLoading && !this.state.summaryLoading
    if (metaReady || applyFinished || this.formNeedsSelect2Init()) {
      this.scheduleSelect2Init()
    }
  }

  componentWillUnmount() {
    clearTimeout(this._resultsSlideTimer)
    if (this._select2InitRaf != null) {
      cancelAnimationFrame(this._select2InitRaf)
      this._select2InitRaf = null
    }
    if (this.formRef.current) destroySelect2InForm(this.formRef.current)
  }

  initSelect2() {
    if (!this.formRef.current) return
    destroySelect2InForm(this.formRef.current)
    initSelect2InForm(this.formRef.current, this.handleSelect2Change, this.state.filters)
  }

  handleSelect2Change = (name, val) => {
    this.setState((prev) => {
      const filters = { ...prev.filters }
      if (name === 'group_by') {
        filters.group_by = val || ''
      } else if (name === 'state') {
        filters.state = Array.isArray(val)
          ? val.map(String).filter(Boolean)
          : val
            ? [String(val)]
            : []
      } else if (
        [
          'territory_id',
          'contractor_id',
          'access_object_id',
          'object_group_id',
        ].includes(name)
      ) {
        filters[name] = Array.isArray(val)
          ? val.map((v) => parseInt(v, 10)).filter((n) => !Number.isNaN(n))
          : val
            ? [parseInt(val, 10)]
            : []
      }
      return { filters }
    }, () => {
      this.persistFiltersCache(this.state.filters)
    })
  }

  buildPayload = () => {
    const { filters } = this.state
    return {
      period: filters.period,
      territory_id: filters.territory_id,
      contractor_id: filters.contractor_id,
      access_object_id: filters.access_object_id,
      object_group_id: filters.object_group_id,
      state: filters.state,
      group_by: filters.group_by,
    }
  }

  validateFilters = () => {
    const { filters } = this.state
    if (!filters.period?.from || !filters.period?.to) {
      return 'Укажите период (дата с и дата по)'
    }
    if (!filters.group_by) {
      return 'Укажите значение «Группировать по»'
    }
    return null
  }

  handleApply = () => {
    const err = this.validateFilters()
    if (err) {
      this.setState({
        formError: err,
        summary: null,
        detail: null,
        displayMode: 'summary',
        resultsSlideInstant: true,
      })
      return
    }
    clearTimeout(this._resultsSlideTimer)
    this.setState({
      formError: null,
      summaryLoading: true,
      detail: null,
      detailTitle: '',
      detailLoading: false,
      displayMode: 'summary',
      resultsSlideInstant: true,
    })
    fetchRequestControlSummary(this.buildPayload(), (e, data) => {
      if (e) {
        this.setState({
          summaryLoading: false,
          formError: e.message || String(e),
          summary: null,
        })
        return
      }
      this.setState({ summary: data, summaryLoading: false, resultsSlideInstant: false }, () => {
        this.persistReportCache({
          filters: this.state.filters,
          appliedFilters: this.state.filters,
          summary: data,
          displayMode: 'summary',
        })
      })
    })
  }

  handleReset = () => {
    const { meta } = this.state
    const filters = filtersFromMeta(meta)
    clearRequestControlCache()
    clearTimeout(this._resultsSlideTimer)
    this.setState(
      {
        filters,
        summary: null,
        detail: null,
        detailTitle: '',
        detailLoading: false,
        displayMode: 'summary',
        resultsSlideInstant: true,
        formError: null,
      },
      () => {
        this.scheduleSelect2Init()
      }
    )
  }

  slideToSummary = (instant = false) => {
    clearTimeout(this._resultsSlideTimer)
    if (instant) {
      this.setState({
        displayMode: 'summary',
        resultsSlideInstant: true,
        detail: null,
        detailTitle: '',
        detailLoading: false,
      })
      requestAnimationFrame(() => {
        this.setState({ resultsSlideInstant: false })
      })
      return
    }
    this.setState({ displayMode: 'summary', resultsSlideInstant: false })
    this._resultsSlideTimer = setTimeout(() => {
      this.setState({
        detail: null,
        detailTitle: '',
        detailLoading: false,
      })
    }, RequestControlReport.SLIDE_MS)
  }

  handleBackToSummary = () => {
    this.slideToSummary(false)
  }

  renderDetailHeader = (title) => (
    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
      <h5 className="card-title mb-0">{title}</h5>
      <button
        type="button"
        className="btn btn-sm btn-outline-secondary"
        onClick={this.handleBackToSummary}
      >
        <i className="ti ti-arrow-left me-1" />
        К сводке
      </button>
    </div>
  )

  handleCellClick = (rowKey, columnKey) => {
    const fetchDetail = () => {
      fetchRequestControlDetail(
        this.buildPayload(),
        rowKey,
        columnKey,
        (err, data) => {
          if (err) {
            clearTimeout(this._resultsSlideTimer)
            this.slideToSummary(true)
            this.setState({ formError: err.message || String(err) })
            return
          }
          this.setState({
            detail: data,
            detailTitle: data.title || '',
            detailLoading: false,
          })
        }
      )
    }

    if (this.state.displayMode === 'detail') {
      this.setState({ detailLoading: true, detail: null, detailTitle: '' }, fetchDetail)
      return
    }

    clearTimeout(this._resultsSlideTimer)
    this.setState(
      { detailLoading: true, detail: null, detailTitle: '', resultsSlideInstant: false },
      () => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.setState({ displayMode: 'detail' }, fetchDetail)
          })
        })
      }
    )
  }

  handleFilterFieldChange = (name, value) => {
    this.setState((prev) => ({
      filters: {
        ...prev.filters,
        [name]: value,
      },
    }))
  }

  handlePeriodChange = (period) => {
    this.setState(
      (prev) => ({
        filters: {
          ...prev.filters,
          period: {
            from: period?.from || '',
            to: period?.to || '',
          },
        },
      }),
      () => {
        this.persistFiltersCache(this.state.filters)
      }
    )
  }

  renderMultiSelect(name, label, options, disabled = false) {
    return (
      <div className="col-md-6 col-lg-4 mb-3">
        <label className="form-label">{label}</label>
        <select
          name={name}
          className="form-select js-select2-report"
          multiple
          disabled={disabled}
        >
          {(options || []).map((opt) => (
            <option key={opt.id} value={String(opt.id)}>
              {opt.title}
            </option>
          ))}
        </select>
      </div>
    )
  }

  renderDetailPane() {
    const { detail, detailTitle, detailLoading, summary } = this.state
    if (detailLoading) {
      return (
        <>
          {this.renderDetailHeader('Детализация')}
          <p className="text-muted mb-0">Загрузка…</p>
        </>
      )
    }
    if (!detail?.items?.length) {
      if (detail && detailTitle) {
        return (
          <>
            {this.renderDetailHeader(`Детализация: ${detailTitle}`)}
            <p className="text-muted mb-0">Нет записей</p>
          </>
        )
      }
      return <span className="visually-hidden">Детализация</span>
    }

    const groupBy = summary?.group_by || detail.group_by
    const showTerritoryFirst = groupBy === 'contractor' || groupBy === 'contractor_approval'
    const isApprovalGroup = groupBy === 'contractor_approval'

    return (
      <>
        {this.renderDetailHeader(`Детализация: ${detailTitle}`)}
        <div className="table-responsive">
          <table className="table report_table mb-0">
            <thead>
              <tr>
                <th>Заявка</th>
                {showTerritoryFirst ? <th>Территория</th> : <th>Контрагент</th>}
                <th>Дата начала</th>
                <th>Дата окончания</th>
                <th>Кол-во посетителей</th>
                {!isApprovalGroup && <th>Этап</th>}
                {!isApprovalGroup && <th>Дней на этапе</th>}
                <th>Согласование</th>
              </tr>
            </thead>
            <tbody>
              {detail.items.map((row) => (
                <tr key={row.request_id}>
                  <td>
                    <Link
                      to={`/requests/${row.request_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {row.request_id}
                    </Link>
                  </td>
                  <td>{showTerritoryFirst ? row.territory : row.contractor}</td>
                  <td>{formatDateIso(row.start_datetime)}</td>
                  <td>{formatDateIso(row.end_datetime)}</td>
                  <td className="number">{row.visitor_count}</td>
                  {!isApprovalGroup && <td>{row.state_label}</td>}
                  {!isApprovalGroup && <td className="number">{row.days_on_stage}</td>}
                  <td>
                    {row.approval_id ? (
                      <Link
                        to={`/approvals/${row.approval_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {row.approval_id}
                      </Link>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    )
  }

  renderReportEmptyState() {
    return (
      <div className="report-empty" role="status" aria-live="polite">
        <div className="report-empty__visual">
          <span className="report-empty__ring" aria-hidden />
          <i className="ti ti-chart-bar-off report-empty__icon" aria-hidden />
        </div>
        <p className="report-empty__title">Данных нет</p>
        <p className="report-empty__hint">
          По выбранным фильтрам заявки не найдены. Измените период или условия
          и нажмите «Применить».
        </p>
      </div>
    )
  }

  renderSummaryPane() {
    const { summary } = this.state
    if (!summary) return null

    if (isSummaryEmpty(summary)) {
      return (
        <>
          <h5 className="card-title mb-3">Сводка</h5>
          {this.renderReportEmptyState()}
        </>
      )
    }

    const rowLabel =
      summary.group_by === 'territory' ? 'Территория' : 'Контрагент'
    const columns = getVisibleSummaryColumns(summary)

    return (
      <>
        <h5 className="card-title mb-3">Сводка</h5>
        <div className="table-responsive">
          <table className="table report_table mb-0">
            <thead>
            <tr>
              <th>{rowLabel}</th>
              {columns.map((col) => (
                <th key={col.id} className="report-th-multiline">
                  {formatReportHeaderTitle(col.title)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {summary.rows.map((row) => (
              <tr key={row.key}>
                <td className="report-row-label">{row.label}</td>
                {columns.map((col) => {
                  const count = row.cells[col.id] ?? 0
                  return (
                    <td key={col.id} className="number">
                      {count > 0 ? (
                        <button
                          type="button"
                          className="report-cell-link"
                          onClick={() => this.handleCellClick(row.key, col.id)}
                        >
                          {count}
                        </button>
                      ) : (
                        count
                      )}
                    </td>
                  )
                })}
              </tr>
            ))}
            <tr className="report-totals">
              <td>Итого</td>
              {columns.map((col) => (
                <td key={col.id} className="number">
                  {summary.totals[col.id] ?? 0}
                </td>
              ))}
            </tr>
          </tbody>
          </table>
        </div>
      </>
    )
  }

  renderResultsSection() {
    const { summary, summaryLoading, displayMode, resultsSlideInstant } = this.state

    if (summaryLoading) {
      return (
        <div className="card mb-4 mt-3 report-results-stage">
          <div className="card-body">
            <p className="text-muted mb-0">Формирование отчёта…</p>
          </div>
        </div>
      )
    }

    if (!this.state.summary) return null

    const summaryPane = this.renderSummaryPane()

    const trackClass = [
      'report-results-track',
      displayMode === 'detail'
        ? 'report-results-track--detail'
        : 'report-results-track--summary',
      resultsSlideInstant ? 'report-results-track--no-transition' : '',
    ]
      .filter(Boolean)
      .join(' ')

    return (
      <div className="card mb-4 mt-3 report-results-stage">
        <div className="card-body">
          <div className="report-results-viewport">
            <div className={trackClass}>
              <div className="report-results-pane">{summaryPane}</div>
              <div className="report-results-pane">{this.renderDetailPane()}</div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  renderContent() {
    const { meta, metaLoading, filters, formError } = this.state
    if (metaLoading) {
      return <p className="text-muted">Загрузка…</p>
    }

    const fmeta = meta?.filters || {}

    return (
      <>
        <div className="card mb-4">
          <div className="card-body" ref={this.formRef}>
            <h5 className="card-title mb-3">Фильтры</h5>
            {formError && (
              <div className="alert alert-warning py-2">{formError}</div>
            )}
            <div className="row">
              <div className="col-md-6 col-lg-4 mb-3">
                <label className="form-label">
                  Период <span className="text-danger">*</span>
                </label>
                <PeriodRangeField
                  value={filters.period}
                  onChange={this.handlePeriodChange}
                />
              </div>

              {fmeta.contractor_id?.visible &&
                this.renderMultiSelect(
                  'contractor_id',
                  'Контрагент',
                  fmeta.contractor_id.options,
                  fmeta.contractor_id.readonly
                )}

              {this.renderMultiSelect(
                'territory_id',
                'Территория',
                fmeta.territory_id?.options
              )}

              {this.renderMultiSelect(
                'access_object_id',
                'Посетитель',
                fmeta.access_object_id?.options
              )}

              {this.renderMultiSelect(
                'object_group_id',
                'Группа посетителей',
                fmeta.object_group_id?.options
              )}

              {this.renderMultiSelect('state', 'Этапы', fmeta.state?.options)}

              <div className="col-md-6 col-lg-4 mb-3">
                <label className="form-label">
                  Группировать по <span className="text-danger">*</span>
                </label>
                <select
                  name="group_by"
                  className="form-select js-select2-report"
                >
                  <option value="">—</option>
                  {(fmeta.group_by?.options || []).map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt.title}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="d-flex gap-2 flex-wrap justify-content-end">
              <button
                type="button"
                className="btn btn-primary"
                onClick={this.handleApply}
              >
                <i className="ti ti-search me-1" />
                Применить
              </button>
              <button
                type="button"
                className="btn btn-warning"
                onClick={this.handleReset}
              >
                <i className="ti ti-x me-1" />
                Сброс
              </button>
            </div>
          </div>
        </div>

        {this.renderResultsSection()}
      </>
    )
  }
}

export default wrapView(RequestControlReport, baseStateToProps)
